import React from "react";
import { Offcanvas } from "react-bootstrap";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import { useCourseAssignmentDrawer } from "./useCourseAssignmentDrawer";
import "../../../pages/Ads/Creatives/components/CreateAdModal.css";

interface CourseAssignmentDrawerProps {
  show: boolean;
  onHide: () => void;
  onSuccess?: () => void;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const PORTAL_STYLES = { menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) };

const CourseAssignmentDrawer: React.FC<CourseAssignmentDrawerProps> = ({
  show,
  onHide,
  onSuccess,
}) => {
  const { functions, values, loading } = useCourseAssignmentDrawer({
    show,
    onHide,
    onSuccess,
  });

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    functions.handleSubmit();
  };

  return (
    <Offcanvas show={show} onHide={onHide} placement="end" className="cad-drawer">
      <form onSubmit={handleFormSubmit} noValidate className="cad-drawer__form">

        {/* Header */}
        <div className="cad-modal__header">
          <span className="cad-modal__title">Create Course Assignment</span>
          <button
            type="button"
            className="cad-modal__close"
            onClick={onHide}
            disabled={loading.isSubmitting}
            aria-label="Close"
          >
            <i className="ph-x" />
          </button>
        </div>

        {/* Body */}
        <div className="cad-modal__body">
          <div className="cad-section">
            <h6 className="cad-section__title">Assignment Details</h6>

            <div className="cad-two-col">
              {/* Academy */}
              <div className="cad-field">
                <label className="cad-label">
                  Academy <span className="cad-required">*</span>
                </label>
                <Select
                  classNamePrefix="cad-select"
                  options={values.academyOptions}
                  value={values.selectedAcademy}
                  onChange={(opt) => functions.setSelectedAcademy(opt)}
                  placeholder={loading.isLoadingAcademies ? "Loading..." : "Select academy..."}
                  isLoading={loading.isLoadingAcademies}
                  menuPortalTarget={document.body}
                  styles={PORTAL_STYLES}
                  menuPosition="fixed"
                  isDisabled={loading.isSubmitting || loading.isLoadingAcademies}
                />
                {values.fieldErrors.academy && (
                  <p className="cad-field-error">{values.fieldErrors.academy}</p>
                )}
              </div>

              {/* Course */}
              <div className="cad-field">
                <label className="cad-label">
                  Course <span className="cad-required">*</span>
                </label>
                <AsyncSelect
                  classNamePrefix="cad-select"
                  loadOptions={functions.loadCourseOptions}
                  defaultOptions={!!values.selectedAcademy}
                  value={values.selectedCourse}
                  onChange={(opt) => functions.setSelectedCourse(opt)}
                  placeholder={
                    !values.selectedAcademy
                      ? "Select academy first..."
                      : "Search course..."
                  }
                  noOptionsMessage={({ inputValue }) =>
                    !values.selectedAcademy
                      ? "Select an academy first"
                      : inputValue
                      ? "No courses found"
                      : "Type to search courses"
                  }
                  menuPortalTarget={document.body}
                  styles={PORTAL_STYLES}
                  menuPosition="fixed"
                  isDisabled={loading.isSubmitting || !values.selectedAcademy}
                  key={values.selectedAcademy?.value ?? "no-academy"}
                />
                {values.fieldErrors.course && (
                  <p className="cad-field-error">{values.fieldErrors.course}</p>
                )}
              </div>
            </div>

            {/* Full Name */}
            <div className="cad-field">
              <label className="cad-label">
                Full Name <span className="cad-required">*</span>
              </label>
              <input
                className={`cad-input${values.fieldErrors.fullName ? " cad-input--error" : ""}`}
                type="text"
                value={values.fullName}
                onChange={(e) => functions.handleFullNameChange(e.target.value)}
                placeholder="Enter full name"
                autoComplete="off"
                disabled={loading.isSubmitting}
              />
              {values.fieldErrors.fullName && (
                <p className="cad-field-error">{values.fieldErrors.fullName}</p>
              )}
            </div>

            {/* Phone Number */}
            <div className="cad-field">
              <label className="cad-label">
                Phone Number <span className="cad-required">*</span>
              </label>
              <div className="cad-phone-row">
                <span className="cad-phone-prefix">+91</span>
                <input
                  className={`cad-input cad-input--phone${values.fieldErrors.phoneNo ? " cad-input--error" : ""}`}
                  type="tel"
                  value={values.phoneNo}
                  onChange={(e) => functions.handlePhoneNoChange(e.target.value)}
                  placeholder="Enter phone number"
                  autoComplete="off"
                  maxLength={10}
                  disabled={loading.isSubmitting}
                />
              </div>
              {values.fieldErrors.phoneNo && (
                <p className="cad-field-error">{values.fieldErrors.phoneNo}</p>
              )}
            </div>
          </div>
        </div>

        {/* API-level error */}
        {values.submitError && (
          <p className="cad-modal__api-error">{values.submitError}</p>
        )}

        {/* Footer */}
        <div className="cad-modal__actions">
          <button
            type="button"
            className="cad-modal__btn cad-modal__btn--cancel"
            onClick={onHide}
            disabled={loading.isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="cad-modal__btn cad-modal__btn--confirm"
            disabled={loading.isSubmitting}
          >
            {loading.isSubmitting ? "Creating..." : "Create Assignment"}
          </button>
        </div>

      </form>
    </Offcanvas>
  );
};

export default CourseAssignmentDrawer;
